import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";

import { apiSlice } from "../api/apiSlice";

const cvsAdapter = createEntityAdapter({
  selectId: (data) => data._id,
});

const initialState = cvsAdapter.getInitialState();

export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCandidateCvs: builder.query({
      query: (owner_id) => ({
        url: `/cv/?owner=${owner_id}`,
      }),
      transformResponse: (responseData) => {
        const cvArray = Array.isArray(responseData)
          ? responseData
          : responseData?.data || responseData?.cvs || [];
        return cvsAdapter.setAll(initialState, cvArray);
      },
      providesTags: (result) =>
        result
          ? [
              { type: "Cv", id: "LIST" },
              ...result.ids.map((id) => ({ type: "Cv", id })),
            ]
          : [{ type: "Cv", id: "LIST" }],
    }),

    getCvById: builder.query({
      query: (id) => ({
        url: `/cv/${id}`,
      }),
      transformResponse: (responseData) => {
        return cvsAdapter.setAll(initialState, Array.isArray(responseData) ? responseData : [responseData]);
      },
      providesTags: (result, error, arg) =>
        result?.ids?.length
          ? result.ids.map((id) => ({ type: "Cv", id }))
          : [{ type: "Cv", id: arg }],
    }),

    getSingleCv: builder.query({
      query: (id) => ({
        url: `/cv/${id}`,
      }),
      transformResponse: (responseData) => responseData,
      providesTags: (result, error, arg) => [{ type: "Cv", id: arg }],
    }),

    addNewCV: builder.mutation({
      query: (newCv) => ({
        url: "/cv",
        method: "POST",
        body: { ...newCv },
      }),
      invalidatesTags: [{ type: "Cv", id: "LIST" }],
    }),

    deleteCv: builder.mutation({
      query: (id) => ({
        url: `/cv/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cv"],
    }),

    /**
     * Unified PATCH for CV.
     * Payload: { cvId, ...fields }
     * Scalar: summary, portfolio, gitRepo, mainRole, mainroles, publishStatus, etc.
     * Array: certifications, projects, etc. - items with id+delete:true = remove,
     *        items with id = update, items without id = add.
     */
    patchCv: builder.mutation({
      query: ({ cvId, ...body }) => ({
        url: `/cv/${cvId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (result, error, arg) =>
        arg?.cvId ? [{ type: "Cv", id: arg.cvId }, { type: "Cv", id: "LIST" }] : [{ type: "Cv", id: "LIST" }],
    }),
  }),
});

export const {
  useGetCandidateCvsQuery,
  useGetCvByIdQuery,
  useAddNewCVMutation,
  useDeleteCvMutation,
  usePatchCvMutation,
  useGetSingleCvQuery,
} = extendedApiSlice;

export const selectCandidateCvsResult =
  extendedApiSlice.endpoints.getCandidateCvs.select();

const selectCandidateCVsData = createSelector(
  selectCandidateCvsResult,
  (cvsResult) => cvsResult?.data
);

export const {
  selectAll: selectAllCandidateCvs,
  selectById: selectCandidateCvByCvId,
  selectIds: selectCandidateCvIds,
  selectTotal: selectItemsInCvAndGiveTheTotal,
} = cvsAdapter.getSelectors(
  (state) => selectCandidateCVsData(state) ?? initialState
);
